import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./stylesPoker.css";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";

import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";

import { BRAND_ICONS_URLS, KPI_CARD_BACKGROUND, USER_HEADER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";

import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";

import { Input, Select, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useSelector } from "react-redux";
import { formatDateNoTimezone } from "../../../../utils/dateService";
import DataTable from "../../../shared/dashboards/DataTable/DataTable";
import useExternalTableFilters from "../../../shared/table-filters/useExternalTableFilters";

import { useGetUserPokerGames } from "../../../../queries/user-manager/useGetUserPokerGames";
import { useGetUserPokerStats } from "../../../../queries/user-manager/useGetUserPokerStats";
import { formatCurrency } from "../../../../utils/financialFormatters";

const DEFAULT_PAGE_SIZE = 20;
// Ant Design's built-in Table.pagination expects `pageSizeOptions` as strings.
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const mapGameRow = (game, fallbackMoneyType) => ({
  key: game?.game_id,
  game_id: game?.game_id || "",
  start_time: game?.game_start_time || "-",
  player_join_time: game?.player_join_time || "-",
  leave_time: game?.player_leave_time || "-",
  buy_in_amount: game?.total_buy_in ?? 0,
  cashout_amount: game?.total_cash_out ?? 0,
  nickname: game?.nickname || "-",
  seat_number: game?.seat_number ?? "-",
  number_of_hands_played: game?.hands_played ?? 0,
  game_turnover: game?.total_game_turnover ?? 0,
  game_pl: game?.profit ?? 0,
  status: game?.game_status || "-",
  money_type: game?.currency_type || fallbackMoneyType,
  wagerAmount: game?.bet_amount ?? "-",
});

function PokerTable({ userData, currencySymbol }) {
  const { appConfig } = useSelector((state) => state.config);
  const primaryCurrencyLabel =
    appConfig?.app_currency_config?.primary_currency?.shortcode || "Cash";
  const secondaryCurrencyLabel =
    appConfig?.app_currency_config?.secondary_currency?.shortcode || "Coin";
  const hasSecondaryCurrency = Boolean(
    appConfig?.app_currency_config?.secondary_currency?.shortcode
  );
  const navigate = useNavigate();

  // ─── Input state: drives the query keys ──────────────────────────────
  const [casinoBetsDates, setCasinoBetsDates] = useState(null);
  // `dateRange` is the API-shaped version of `casinoBetsDates` (null = all-time).
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Cursor pagination: backend returns `next_cursor` for the *next* page.
  // `{ [page]: cursorToReachThatPage }` lets the user go back without refetch.
  const [cursorsByPage, setCursorsByPage] = useState({ 1: null });
  // Committed search value; the input value lives in `filters.searchValue`.
  const [appliedSearchValue, setAppliedSearchValue] = useState("");
  const [filterType, setFilterType] = useState("primary");
  const {
    filters,
    normalizedFilters,
    setFilter,
  } = useExternalTableFilters({
    searchValue: "",
    moneyType: "rebet_cash",
  });

  const moneyTypeOptions = [
    { label: "Cash", value: "rebet_cash" },
    { label: "Coin", value: "rebet_coin" },
  ];

  // ─── Queries ─────────────────────────────────────────────────────────
  const cursor = cursorsByPage[currentPage] ?? null;

  const gamesQuery = useGetUserPokerGames(
    userData?.user_id
      ? {
          user_id: userData.user_id,
          page: currentPage,
          limit: pageSize,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          money_type: filters.moneyType,
          cursor,
          game_id: appliedSearchValue || null,
        }
      : null,
  );

  const statsQuery = useGetUserPokerStats(
    userData?.user_id
      ? {
          user_id: userData.user_id,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          money_type: filters.moneyType,
        }
      : null,
  );

  // ─── Derived data (replaces the old useState mirrors) ────────────────
  const betsToShowInTable = useMemo(
    () =>
      (gamesQuery.data?.data ?? []).map((game) =>
        mapGameRow(game, filters.moneyType),
      ),
    [gamesQuery.data, filters.moneyType],
  );
  const apiPagination = gamesQuery.data?.pagination ?? {};
  const pagination = {
    current_page: apiPagination.current_page ?? currentPage,
    // Local state wins over the BE echo: the dropdown must stay pinned to
    // the admin's selection even if the BE ignores the `limit` param.
    page_size: pageSize,
    has_next: apiPagination.has_next ?? false,
    has_prev: apiPagination.has_previous ?? false,
  };
  const summary = {
    total_buy_in: statsQuery.data?.total_buy_in ?? 0,
    total_cash_out: statsQuery.data?.total_cash_out ?? 0,
    total_net_revenue: statsQuery.data?.net_revenue ?? 0,
  };
  const isApiRunning = gamesQuery.isFetching;
  const isKpisLoading = statsQuery.isFetching;
  const resetTable = Boolean(appliedSearchValue);

  // ─── Side effects ────────────────────────────────────────────────────
  // Cache the `next_cursor` so a future "Next" click hits the right page.
  // With restrictToAdjacentPages, a user on page N can step to N-1 or N+1,
  // so keep a three-entry window: previous, current, next. Without the
  // previous cursor, navigating back to page N-1 sends a null cursor and
  // the API returns page-1 data (regression from nit #2132).
  useEffect(() => {
    const nextCursor = gamesQuery.data?.pagination?.next_cursor;
    if (!nextCursor) return;
    setCursorsByPage((prev) => {
      if (prev[currentPage + 1] === nextCursor) return prev;
      return {
        [currentPage - 1]: prev[currentPage - 1],
        [currentPage]: prev[currentPage],
        [currentPage + 1]: nextCursor,
      };
    });
  }, [gamesQuery.data, currentPage]);

  // Expired-token handling — kicks the user to login if either query 401s.
  useEffect(() => {
    if (gamesQuery.error) {
      handleExpiredTokenError(navigate, gamesQuery.error?.response);
    }
  }, [gamesQuery.error, navigate]);
  useEffect(() => {
    if (statsQuery.error) {
      handleExpiredTokenError(navigate, statsQuery.error?.response);
    }
  }, [statsQuery.error, navigate]);

  // ─── Handlers ────────────────────────────────────────────────────────
  const resetPagination = () => {
    setCurrentPage(1);
    setCursorsByPage({ 1: null });
  };

  const handleSearchValueChange = (event) => {
    setFilter("searchValue", event?.target?.value);
  };

  const handleSearchPressed = () => {
    if (!normalizedFilters.searchValue) return;
    setAppliedSearchValue(normalizedFilters.searchValue);
    resetPagination();
  };

  const money = (value) => formatCurrency(Number(value ?? 0), currencySymbol, 2);

  const columns = [
    {
      title: AppStrings.GAME_ID,
      dataIndex: "game_id",
      key: "game_id",
      width: 100,
      render: (text) => {
        return <p className="game-id-text">{text}</p>;
      },
    },
    {
      title: AppStrings.START_TIME,
      dataIndex: "start_time",
      key: "start_time",
      width: 150,
      render: (text) =>
        text && text !== "-"
          ? formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss")
          : "-",
    },
    {
      title: AppStrings.PLAYER_JOIN_TIME,
      dataIndex: "player_join_time",
      key: "player_join_time",
      width: 150,
      render: (text) =>
        text && text !== "-"
          ? formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss")
          : "-",
    },
    {
      title: AppStrings.LEAVE_TIME,
      dataIndex: "leave_time",
      key: "leave_time",
      width: 150,
      render: (text) =>
        text && text !== "-"
          ? formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss")
          : "-",
    },
    {
      title: AppStrings.BUY_IN_AMOUNT,
      dataIndex: "buy_in_amount",
      key: "buy_in_amount",
      width: 150,
      render: (text) => <p style={{ margin: 0 }}>{money(text)}</p>,
    },
    {
      title: AppStrings.CASHOUT_AMOUNT,
      dataIndex: "cashout_amount",
      key: "cashout_amount",
      width: 200,
      render: (text) => <p style={{ margin: 0 }}>{money(text)}</p>,
    },

    {
      title: AppStrings.NICKNAME,
      dataIndex: "nickname",
      key: "nickname",
      width: 150,
    },
    {
      title: AppStrings.SEAT_NUMBER,
      dataIndex: "seat_number",
      key: "seat_number",
      width: 150,
    },
    {
      title: AppStrings.NUMBER_OF_HANDS_PLAYED,
      dataIndex: "number_of_hands_played",
      key: "number_of_hands_played",
      width: 250,
    },
    {
      title: AppStrings.GAME_TURNOVER,
      dataIndex: "game_turnover",
      key: "game_turnover",
      width: 150,
      render: (text) => <p style={{ margin: 0 }}>{money(text)}</p>,
    },
    {
      title: AppStrings.GAME_P_L,
      dataIndex: "game_pl",
      key: "game_pl",
      width: 150,
      render: (text) => <p style={{ margin: 0 }}>{money(text)}</p>,
    },
    {
      title: AppStrings.STATUS,
      dataIndex: "status",
      key: "status",
      width: 150,
    },
    {
      title: () => (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span>{AppStrings.BET_AMOUNT}</span>
          {hasSecondaryCurrency && (
            <div className="bet-amount-buttons">
              <Button
                className={filterType === "primary" ? "active" : ""}
                onClick={() => setFilterType("primary")}
              >
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.primaryCurrencySymbol
                  }
                  alt={primaryCurrencyLabel}
                  style={{ width: "20px", height: "auto" }}
                />
                <span>{primaryCurrencyLabel}</span>
              </Button>
              <Button
                className={filterType === "secondary" ? "active" : ""}
                onClick={() => setFilterType("secondary")}
              >
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.secondaryCurrencySymbol
                  }
                  alt={secondaryCurrencyLabel}
                  style={{ width: "20px", height: "auto" }}
                />
                <span>{secondaryCurrencyLabel}</span>
              </Button>
            </div>
          )}
        </div>
      ),
      dataIndex: "wagerAmount",
      key: "wagerAmount",
      width: 300,
      render: (text, record) => (
        <div className="bets-report-bet-amount-container" style={{ display: "flex"}}>
          {record.money_type === "rebet_cash" ? (
            <img src={process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL + BRAND_ICONS_URLS.primaryCurrencySymbol} alt="Cash" style={{ width: "20px", height: "auto" }}/>
          ) : record.money_type === "rebet_coin" ? (
              <img src={process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL + BRAND_ICONS_URLS.secondaryCurrencySymbol} alt="Coin" style={{ width: "20px", height: "auto" }}/>
          ) : (
            ""
          )}
          <span className="bets-report-bet-amount-text">
            {record.wagerAmount === "-"
              ? "-"
              : Intl.NumberFormat("en-US", {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                }).format(record.wagerAmount)}
          </span>
        </div>
      ),
      onCell: () => ({
        style: {
          verticalAlign: "middle",
        },
      }),
    },
  ];
  const handleDateChange = (queryString) => {
    if (queryString) {
      const [start, end] = queryString.split("&");
      setDateRange({
        startDate: dayjs(start).format("YYYY-MM-DD"),
        endDate: dayjs(end).format("YYYY-MM-DD"),
      });
    } else {
      setDateRange({ startDate: null, endDate: null });
    }
    setFilter("searchValue", "");
    setAppliedSearchValue("");
    resetPagination();
  };

  const handleResetTable = () => {
    setCasinoBetsDates(null);
    setDateRange({ startDate: null, endDate: null });
    setFilter("searchValue", "");
    setAppliedSearchValue("");
    resetPagination();
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    // Cursor-based pagination assumes a fixed page_size, so a change
    // must invalidate every cached cursor and restart from page 1.
    resetPagination();
  };

  // Ant Design's Table.pagination.onChange also fires when the user
  // switches page size — in that case `nextSize` differs from the current
  // state. Route that through handlePageSizeChange so cursor invariants
  // stay in sync.
  const handlePaginationChange = (nextPage, nextSize) => {
    if (nextSize !== pageSize) {
      handlePageSizeChange(nextSize);
      return;
    }
    // Cursor-based pagination only knows the next cursor — restrict jumps
    // to adjacent pages. Non-adjacent clicks would send a null cursor and
    // regress to page-1 data.
    if (Math.abs(nextPage - currentPage) > 1) return;
    setCurrentPage(nextPage);
  };

  const handleChangeMoneyType = (value) => {
    setFilter("moneyType", value);
    // `filterType` drives the "primary"/"secondary" currency toggle in the
    // bet-amount column header. Map the API money_type into that domain so
    // the active button reflects the dropdown selection.
    setFilterType(value === "rebet_coin" ? "secondary" : "primary");
    setAppliedSearchValue("");
    resetPagination();
  };

  return (
    <div className="poker-bets-container">
      <div className="poker-bets-header">
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_BUY_IN}
            value={summary?.total_buy_in}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.TOTAL_CASH_OUT}
            value={summary?.total_cash_out}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isKpisLoading}
            title={AppStrings.PLAYER_NET_REVENUE}
            value={summary?.total_net_revenue}
            hugeValue={false}
            percentage={false}
            currencySymbol={currencySymbol}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={casinoBetsDates}
          setDates={setCasinoBetsDates}
          showPresets={true}
          startRange={AppStrings.TODAY}
        />
      </div>
      <div className="poker-bets-table-container">
        <div className="head-controls poker-bets-table-header-container">
          <div className="poker-bets-table-header-row">
            <div className="header-text-container">
              <p className="header-text">
                {AppStrings.USERNAME_POKER_GAMES?.replace(
                  "{username}",
                  userData?.username
                )}
              </p>
            </div>

            <Input
              type="text"
              placeholder={AppStrings.SEARCH_BY_GAME_ID}
              className="bet-searchInput"
              value={filters.searchValue}
              onChange={handleSearchValueChange}
            />
            <button className="search-button" data-testid="search-button" onClick={handleSearchPressed}>
              <SearchOutlined style={{ fontSize: "20px", color: "white" }} />
            </button>
          </div>
          {hasSecondaryCurrency && (
            <div className="poker-bets-table-filter-row">
              <div className="drop-down-container">
                <div className="casino-selector">
                  <Select
                    placeholder={AppStrings.MONEY_TYPE}
                    options={moneyTypeOptions}
                    value={filters.moneyType}
                    onChange={handleChangeMoneyType}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="">
          {isApiRunning ? (
            <RebetLoader height={"100px"} width={"100px"} />
          ) : (
            <div className="poker-bets-table">
              <DataTable
                dataSource={betsToShowInTable}
                columns={columns}
                scrollPreset="ultraWide"
                pagination={
                  !resetTable && (pagination.has_next || pagination.has_prev)
                    ? {
                        current: pagination.current_page,
                        pageSize: pagination.page_size,
                        // BE returns `total_games: null` — the count is
                        // unknown. Estimate one past the current page when
                        // there's a next cursor so the "Next" button lights
                        // up, otherwise count the current page as the last.
                        total: pagination.has_next
                          ? pagination.current_page * pagination.page_size + 1
                          : pagination.current_page * pagination.page_size,
                        onChange: (nextPage, nextSize) =>
                          handlePaginationChange(nextPage, nextSize),
                        showSizeChanger: true,
                        pageSizeOptions: [...PAGE_SIZE_OPTIONS],
                        showQuickJumper: false,
                        hideOnSinglePage: true,
                        itemRender: (page, type, originalElement) => {
                          // Cursor-based: only ±1 from current is reachable,
                          // so dim far-away page numbers and hide "..." jumpers.
                          if (type === "page") {
                            const isDisabled =
                              Math.abs(page - pagination.current_page) > 1;
                            return isDisabled ? (
                              <span
                                style={{ opacity: 0.4, pointerEvents: "none" }}
                              >
                                {page}
                              </span>
                            ) : (
                              originalElement
                            );
                          }
                          if (type === "jump-prev" || type === "jump-next")
                            return null;
                          return originalElement;
                        },
                      }
                    : false
                }
                onRow={(record) => ({
                  onClick: () => {
                    if (!userData?.user_id || !record?.game_id) {
                      return;
                    }
                    localStorage.setItem(
                      "selected_user_data",
                      JSON.stringify(userData)
                    );
                    localStorage.setItem("wallet_active", "false");
                    const params = new URLSearchParams({
                      userId: userData.user_id,
                      tab: USER_HEADER_TABS.POKER_BETS,
                      game_id: record.game_id,
                    });
                    window.open(
                      `${NAVIGATION_ROUTES.USER_MANAGER}?${params.toString()}`,
                      "_blank"
                    );
                  },
                  className: "poker-bets-table-row",
                })}
              />
              {resetTable && (
                <div className="reset-table-button-container">
                  <SaveButton
                    buttonText={AppStrings.RESET_TABLE}
                    className="reset-table-button"
                    callbackFunction={handleResetTable}
                    isLoading={isApiRunning}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PokerTable;
